import { useMemo, useCallback, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { AddFeatureControl, RuleSetNotesHover } from '../Shared';
import ModalPortal from '../../../../components/ModalPortal';
import { getSelectedValues, toggleValue, getUnusedValuesForHeader, isEmptyRuleCellValue, rulesetHasEmptyCells, cellMatchesSearchValues, filterFeaturesForFamily, resolveFamilyIdsForFeatureLookup } from '../../utils';
import { useToolAccess } from '../../../../tools/ToolAccessContext';

const displayRuleCellValue = (value) => value === "*" ? "" : (value ?? "");

function TableFocusIcon({ expanded }) {
    return (
        <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
        >
            {expanded ? (
                <>
                    <polyline points="4 14 10 14 10 20" />
                    <polyline points="20 10 14 10 14 4" />
                    <line x1="14" y1="10" x2="21" y2="3" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                </>
            ) : (
                <>
                    <polyline points="15 3 21 3 21 9" />
                    <polyline points="9 21 3 21 3 15" />
                    <line x1="21" y1="3" x2="14" y2="10" />
                    <line x1="3" y1="21" x2="10" y2="14" />
                </>
            )}
        </svg>
    );
}

/**
 * RuleEditor - Main rule table editing component
 * Handles the Excel-like grid for editing rule table data
 */
export default function RuleEditor({
    draftRuleSetData,
    activeRuleSetData,
    activeRuleSetId,
    isRuleSetLoading = false,
    ruleSetLoadFailed = false,
    featuresMetadata,
    focusedCell,
    isEditing,
    editingCellValue,
    dropdownCoords,
    highlightedOptionIndex,
    families,
    currentContext,
    isRightPaneOpen,
    setIsRightPaneOpen,
    isTableFocusMode = false,
    setIsTableFocusMode,
    handleCellKeyDown,
    handleCommitCell,
    handleEditorKeyDown,
    handleDeleteRowLocally,
    handleAddFeatureLocally,
    handleDeleteFeatureLocally,
    handleSaveChanges,
    setFocusedCell,
    setIsEditing,
    setEditingCellValue,
    setHighlightedOptionIndex,
    hasChanges,
    originalRuleSetData,
    setDraftRuleSetData,
    blocker,
    ruleSetNotes = [],
    searchHighlight = null
}) {
    const editorRootRef = useRef(null);

    const filteredFeaturesMetadata = useMemo(() => {
        const familyIds = resolveFamilyIdsForFeatureLookup(activeRuleSetData, currentContext);
        return filterFeaturesForFamily(featuresMetadata, familyIds);
    }, [featuresMetadata, activeRuleSetData, currentContext]);

    const activeHighlight = searchHighlight
        && draftRuleSetData
        && String(searchHighlight.rulesetId) === String(draftRuleSetData.id)
        ? searchHighlight
        : null;
    const highlightedColumns = (activeHighlight?.columns || []).map(name => name.toLowerCase());
    const highlightedValues = activeHighlight?.values || [];

    useEffect(() => {
        if (!activeHighlight) return undefined;
        const timeoutId = window.setTimeout(() => {
            const root = editorRootRef.current;
            if (!root) return;
            const target = root.querySelector('[data-search-hit="cell"]')
                || root.querySelector('[data-search-hit="column"]')
                || root.querySelector('[data-search-hit="comment"]')
                || root.querySelector('[data-search-hit="name"]');
            if (target && typeof target.scrollIntoView === 'function') {
                target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
            }
        }, 50);
        return () => window.clearTimeout(timeoutId);
    }, [activeHighlight]);

    useEffect(() => {
        if (!isTableFocusMode) return undefined;
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            if (isEditing) return;
            if (document.querySelector('.modal-overlay')) return;
            event.preventDefault();
            setIsTableFocusMode?.(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isTableFocusMode, isEditing, setIsTableFocusMode]);

    const draftReady = Boolean(
        activeRuleSetId
        && draftRuleSetData
        && String(draftRuleSetData.id) === String(activeRuleSetId)
    );
    const showRuleSetLoader = Boolean(activeRuleSetId)
        && !ruleSetLoadFailed
        && (isRuleSetLoading || !draftReady);

    if (showRuleSetLoader) {
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-page)' }}>
                <div style={{
                    padding: '1.5rem 2rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    background: 'var(--bg-card)',
                    flexShrink: 0
                }}></div>
                <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div className="loader" role="status" aria-label="Loading rule set"></div>
                </div>
            </div>
        );
    }

    // If no draft data, show empty state
    if (!draftRuleSetData) {
        return (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-page)' }}>
                {/* Header */}
                <div style={{
                    padding: '1.5rem 2rem',
                    borderBottom: '1px solid var(--border)',
                    display: 'flex',
                    justifyContent: 'flex-end',
                    background: 'var(--bg-card)',
                    flexShrink: 0
                }}></div>

                {/* Empty state */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '2rem' }}>
                    <div style={{ display: 'flex', height: '100%', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', flexDirection: 'column', gap: '1rem' }}>
                        <i className="fa-regular fa-table" style={{ fontSize: '3rem', color: 'var(--text-secondary)', opacity: 0.5 }}></i>
                        <span>Select a Rule Set to View</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div ref={editorRootRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', background: 'var(--bg-page)' }}>
            
            {/* ============================================================ */}
            {/* HEADER */}
            {/* ============================================================ */}
            <div style={{
                padding: '1.5rem 2rem',
                borderBottom: '1px solid var(--border)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                background: 'var(--bg-card)',
                flexShrink: 0
            }}>
                <div>
                    <h1 style={{ fontSize: '1.5rem', fontWeight: 700, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span
                            data-search-hit={activeHighlight?.highlightName ? 'name' : undefined}
                            className={activeHighlight?.highlightName ? 'search-hit' : undefined}
                            style={activeHighlight?.highlightName ? { padding: '0.1rem 0.35rem', borderRadius: '6px' } : undefined}
                        >
                            {draftRuleSetData.name}
                        </span>
                        {rulesetHasEmptyCells(draftRuleSetData) && (
                            <i
                                className="fa-solid fa-triangle-exclamation"
                                title="This rule table has empty cells."
                                style={{ color: '#D97706', fontSize: '1rem' }}
                            ></i>
                        )}
                    </h1>
                    <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.25rem', margin: 0 }}>
                        Used in: {activeRuleSetData?.sourceFamilyIds?.length > 0 ? 
                            activeRuleSetData.sourceFamilyIds.map(famId => {
                                const fam = families.find(f => f.id === famId);
                                return fam ? fam.name : famId;
                            }).join(', ') : 'Global Domain'}
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                    <RuleSetNotesHover
                        notes={ruleSetNotes}
                        highlightTexts={activeHighlight?.comments || []}
                        highlightKey={activeHighlight?.nonce}
                    />
                    <AddFeatureControl 
                        featuresMetadata={filteredFeaturesMetadata} 
                        currentHeaders={draftRuleSetData.headers} 
                        onAdd={handleAddFeatureLocally}
                    />
                    <button
                        type="button"
                        className="btn-secondary"
                        onClick={() => setIsTableFocusMode?.(prev => !prev)}
                        title={isTableFocusMode ? 'Exit focus mode' : 'Expand table'}
                        aria-label={isTableFocusMode ? 'Exit focus mode' : 'Expand table'}
                        aria-pressed={isTableFocusMode}
                        style={{ fontSize: '0.85rem', display: 'flex', alignItems: 'center', padding: '0.5rem 0.75rem' }}
                    >
                        <TableFocusIcon expanded={isTableFocusMode} />
                    </button>
                </div>
            </div>

            {/* ============================================================ */}
            {/* TABLE CONTAINER */}
            {/* ============================================================ */}
            <div className="rule-editor-scroll" style={{ flex: 1, overflowY: 'auto', padding: isTableFocusMode ? '0.75rem' : '2rem', scrollbarGutter: 'stable' }}>
                <div className="table-container" style={{ overflowX: 'auto', border: '1px solid var(--border)', borderRadius: 'var(--radius)' }}>
                    <table className="data-grid" style={{ margin: 0, outline: 'none' }}>
                        
                        {/* ======================================================== */}
                        {/* THEAD - Feature column headers */}
                        {/* ======================================================== */}
                        <thead>
                            <tr>
                                <th style={{ width: '80px', textAlign: 'center' }}>Actions</th>
                                {draftRuleSetData.headers.map(h => {
                                    const featureMeta = filteredFeaturesMetadata.find(v => v.name === h);
                                    const isGlobal = featureMeta?.isGlobal;
                                    const isColumnHit = highlightedColumns.includes(h.toLowerCase());
                                    return (
                                        <th
                                            key={h}
                                            className={`grid-header-cell${isColumnHit ? ' search-hit' : ''}`}
                                            data-search-hit={isColumnHit ? 'column' : undefined}
                                        >
                                            <span className="header-content">
                                                {isGlobal && <i className="fa-solid fa-globe" title="Global Feature" style={{ color: 'var(--accent)', fontSize: '0.9rem' }}></i>}
                                                <span>{h}</span>
                                            </span>
                                            <button 
                                                onClick={() => handleDeleteFeatureLocally(h)} 
                                                className="delete-col-btn"
                                                title="Remove Feature"
                                            >
                                                <i className="fa-solid fa-trash-can"></i>
                                            </button>
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>

                        {/* ======================================================== */}
                        {/* TBODY - Rule rows with editable cells */}
                        {/* ======================================================== */}
                        <tbody>
                            {draftRuleSetData.rows.map((row, rowIndex) => {
                                const rowHasValueHit = highlightedValues.length > 0 && (row.cells || []).some(cell => {
                                    const header = cell.featureName ?? cell.FeatureName ?? '';
                                    const columnOk = highlightedColumns.length === 0 || highlightedColumns.includes(String(header).toLowerCase());
                                    return columnOk && cellMatchesSearchValues(cell.expectedValue ?? cell.ExpectedValue, highlightedValues);
                                });
                                return (
                                <tr key={rowIndex} className={rowHasValueHit ? 'search-hit-row' : undefined}>
                                    {/* Delete row button */}
                                    <td style={{ textAlign: "center" }}>
                                        <button 
                                            onClick={() => handleDeleteRowLocally(rowIndex)} 
                                            className="delete-row-btn"
                                            title="Delete Row"
                                        >
                                            <i className="fa-solid fa-trash-can"></i>
                                        </button>
                                    </td>

                                    {/* Feature cells */}
                                    {draftRuleSetData.headers.map((header, colIdx) => {
                                        const colIndex = colIdx + 1; // colIndex starts at 1
                                        const cell = row.cells.find(c => c.featureName === header);
                                        const valueToShow = cell ? displayRuleCellValue(cell.expectedValue) : '';
                                        
                                        const isFocused = focusedCell?.rowIndex === rowIndex && focusedCell?.colIndex === colIndex;
                                        const isCellEditing = isEditing && isFocused;
                                        
                                        const featureMeta = filteredFeaturesMetadata.find(v => v.name === header);

                                        const isColumnHit = highlightedColumns.includes(header.toLowerCase());
                                        const isValueHit = cell
                                            && (highlightedColumns.length === 0 || isColumnHit)
                                            && cellMatchesSearchValues(cell.expectedValue, highlightedValues);

                                        return (
                                            <td 
                                                key={header}
                                                data-cell-row={rowIndex}
                                                data-cell-col={colIndex}
                                                data-search-hit={isValueHit ? 'cell' : (isColumnHit ? 'column' : undefined)}
                                                tabIndex={0}
                                                onFocus={() => setFocusedCell({ rowIndex, colIndex })}
                                                onKeyDown={(e) => handleCellKeyDown(e, rowIndex, colIndex)}
                                                onDoubleClick={() => setIsEditing(true)}
                                                className={`grid-cell ${isFocused ? 'focused' : ''}${isValueHit ? ' search-hit' : ''}${isColumnHit && !isValueHit ? ' search-hit-col' : ''}`}
                                                style={{
                                                    outline: isFocused ? '2px solid var(--accent)' : 'none',
                                                    background: isFocused ? 'rgba(99, 102, 241, 0.15)' : undefined,
                                                    position: 'relative',
                                                    cursor: 'pointer',
                                                    minWidth: '120px',
                                                    padding: isCellEditing ? '0px' : '0.5rem 0.75rem',
                                                    verticalAlign: 'middle'
                                                }}
                                            >
                                                {isCellEditing ? (
                                                    <RuleCellEditor
                                                        featureMeta={featureMeta}
                                                        editingCellValue={editingCellValue}
                                                        setEditingCellValue={setEditingCellValue}
                                                        handleCommitCell={() => handleCommitCell(rowIndex, header)}
                                                        handleEditorKeyDown={(e) => handleEditorKeyDown(e, rowIndex, header)}
                                                        dropdownCoords={dropdownCoords}
                                                        highlightedOptionIndex={highlightedOptionIndex}
                                                        setHighlightedOptionIndex={setHighlightedOptionIndex}
                                                        toggleValue={toggleValue}
                                                        getSelectedValues={getSelectedValues}
                                                    />
                                                ) : (
                                                    <div style={{ userSelect: 'none', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                                        {cell && isEmptyRuleCellValue(cell.expectedValue) && (
                                                            <i
                                                                className="fa-solid fa-triangle-exclamation"
                                                                title="This cell is empty."
                                                                style={{ color: '#D97706', fontSize: '0.75rem', flexShrink: 0 }}
                                                            ></i>
                                                        )}
                                                        {valueToShow}
                                                    </div>
                                                )}
                                            </td>
                                        );
                                    })}
                                </tr>
                                );
                            })}

                            {/* ==================================================== */}
                            {/* VIRTUAL ROW - For adding new rows */}
                            {/* ==================================================== */}
                            <VirtualRow
                                draftRuleSetData={draftRuleSetData}
                                featuresMetadata={filteredFeaturesMetadata}
                                focusedCell={focusedCell}
                                isEditing={isEditing}
                                editingCellValue={editingCellValue}
                                dropdownCoords={dropdownCoords}
                                highlightedOptionIndex={highlightedOptionIndex}
                                setFocusedCell={setFocusedCell}
                                setIsEditing={setIsEditing}
                                setEditingCellValue={setEditingCellValue}
                                setHighlightedOptionIndex={setHighlightedOptionIndex}
                                handleCellKeyDown={handleCellKeyDown}
                                handleCommitCell={handleCommitCell}
                                handleEditorKeyDown={handleEditorKeyDown}
                                toggleValue={toggleValue}
                                getSelectedValues={getSelectedValues}
                            />
                        </tbody>

                        {/* ======================================================== */}
                        {/* TFOOT - Unused values */}
                        {/* ======================================================== */}
                        <tfoot>
                            <tr style={{ background: 'var(--bg-secondary)', borderTop: '2px solid var(--border)' }}>
                                <td style={{
                                    textAlign: "center", 
                                    fontWeight: "600", 
                                    fontSize: "0.8rem", 
                                    color: 'var(--text-secondary)', 
                                    fontFamily: 'sans-serif',
                                    padding: '0.75rem 0.5rem',
                                    verticalAlign: 'middle'
                                }}>
                                    Unused
                                </td>
                                {draftRuleSetData.headers.map(header => {
                                    const unused = getUnusedValuesForHeader(header, draftRuleSetData, filteredFeaturesMetadata);
                                    return (
                                        <td key={header} style={{ padding: "0.5rem 0.75rem", verticalAlign: "top" }}>
                                            {unused === null ? (
                                                <span style={{ color: 'var(--text-secondary)', opacity: 0.6, fontSize: '0.75rem' }}>-</span>
                                            ) : unused.length > 0 ? (
                                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                                                    {unused.map(val => (
                                                        <span key={val} style={{
                                                            background: 'var(--bg-primary)',
                                                            border: '1px solid var(--border)',
                                                            borderRadius: '4px',
                                                            padding: '2px 6px',
                                                            fontSize: '0.7rem',
                                                            fontFamily: 'monospace',
                                                            color: 'var(--text-secondary)',
                                                            display: 'inline-block'
                                                        }}>
                                                            {val}
                                                        </span>
                                                    ))}
                                                </div>
                                            ) : (
                                                <span style={{ fontStyle: 'italic', color: 'var(--text-secondary)', opacity: 0.6, fontSize: '0.75rem' }}>None</span>
                                            )}
                                        </td>
                                    );
                                })}
                            </tr>
                        </tfoot>
                    </table>
                </div>

                {/* ============================================================ */}
                {/* UNSAVED CHANGES ALERT */}
                {/* ============================================================ */}
                {hasChanges && (
                    <UnsavedChangesAlert
                        originalRuleSetData={originalRuleSetData}
                        handleSaveChanges={handleSaveChanges}
                        setDraftRuleSetData={setDraftRuleSetData}
                    />
                )}

                {/* ============================================================ */}
                {/* NAVIGATION BLOCKER MODAL */}
                {/* ============================================================ */}
                {blocker && (
                    <NavigationBlockerModal
                        blocker={blocker}
                        hasChanges={hasChanges}
                        originalRuleSetData={originalRuleSetData}
                        handleSaveChanges={handleSaveChanges}
                        setDraftRuleSetData={setDraftRuleSetData}
                    />
                )}
            </div>
        </div>
    );
}

// ========================================================================
// SUB-COMPONENTS
// ========================================================================

/**
 * RuleCellEditor - Input for editing cell values
 */
function RuleCellEditor({
    featureMeta,
    editingCellValue,
    setEditingCellValue,
    handleCommitCell,
    handleEditorKeyDown,
    dropdownCoords,
    highlightedOptionIndex,
    setHighlightedOptionIndex,
    toggleValue,
    getSelectedValues
}) {
    // If no feature metadata, just show a plain input
    if (!featureMeta) {
        return (
            <input
                type="text"
                className="cell-input"
                value={editingCellValue ?? ""}
                onChange={e => setEditingCellValue(e.target.value)}
                onKeyDown={handleEditorKeyDown}
                autoFocus
                onBlur={handleCommitCell}
                style={{ 
                    width: '100%', 
                    border: 'none', 
                    background: 'var(--bg-secondary)', 
                    color: 'var(--text-primary)', 
                    outline: 'none', 
                    padding: '0.5rem 0.75rem', 
                    borderRadius: '4px' 
                }}
            />
        );
    }

    // If we have feature metadata, show input with dropdown
    return (
        <>
            <input
                type="text"
                className="cell-input"
                value={editingCellValue ?? ""}
                onChange={e => setEditingCellValue(e.target.value)}
                onKeyDown={handleEditorKeyDown}
                autoFocus
                onBlur={handleCommitCell}
                style={{ 
                    width: '100%', 
                    border: 'none', 
                    background: 'var(--bg-secondary)', 
                    color: 'var(--text-primary)', 
                    outline: 'none', 
                    padding: '0.5rem 0.75rem', 
                    borderRadius: '4px' 
                }}
            />
            
            {/* Dropdown portal for value selection */}
            {dropdownCoords && (
                <RuleCellDropdown
                    featureMeta={featureMeta}
                    editingCellValue={editingCellValue}
                    setEditingCellValue={setEditingCellValue}
                    highlightedOptionIndex={highlightedOptionIndex}
                    setHighlightedOptionIndex={setHighlightedOptionIndex}
                    dropdownCoords={dropdownCoords}
                    toggleValue={toggleValue}
                    getSelectedValues={getSelectedValues}
                />
            )}
        </>
    );
}

/**
 * RuleCellDropdown - Dropdown for selecting cell values
 */
function RuleCellDropdown({
    featureMeta,
    editingCellValue,
    setEditingCellValue,
    highlightedOptionIndex,
    setHighlightedOptionIndex,
    dropdownCoords,
    toggleValue,
    getSelectedValues
}) {
    const options = useMemo(() => {
        const vals = Array.isArray(featureMeta.values) ? featureMeta.values : [];
        const valueStrings = vals.map(v => v.value !== undefined && v.value !== null ? String(v.value) : '');
        return ["?", "!", ...[...valueStrings].sort((a, b) => a.localeCompare(b))];
    }, [featureMeta.values]);

    const isSelected = useCallback((val) => {
        if (val === "?") {
            return editingCellValue === "?" || editingCellValue === "*";
        }
        if (val === "!") {
            return editingCellValue === "!";
        }
        const selectedList = getSelectedValues(editingCellValue);
        return selectedList.includes(val);
    }, [editingCellValue, getSelectedValues]);

    return createPortal(
        <div 
            className="custom-combobox-dropdown"
            style={{
                position: 'fixed',
                top: dropdownCoords.top != null ? `${dropdownCoords.top}px` : 'auto',
                bottom: dropdownCoords.bottom != null ? `${dropdownCoords.bottom}px` : 'auto',
                left: `${dropdownCoords.left}px`,
                minWidth: `${dropdownCoords.width}px`,
                width: 'max-content',
                maxWidth: '240px',
                background: 'var(--bg-primary)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius)',
                boxShadow: '0 4px 16px rgba(0,0,0,0.5)',
                maxHeight: `${dropdownCoords.maxHeight ?? 180}px`,
                overflowY: 'auto',
                zIndex: 9999,
                padding: '4px'
            }}
        >
            {options.map((val, idx) => {
                const selected = isSelected(val);
                const isHighlighted = idx === highlightedOptionIndex;
                return (
                    <div
                        key={val}
                        onMouseDown={(e) => {
                            e.preventDefault();
                            const newVal = toggleValue(editingCellValue, val);
                            setEditingCellValue(newVal);
                        }}
                        style={{
                            padding: '6px 10px',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            background: isHighlighted 
                                ? 'var(--bg-secondary)' 
                                : selected 
                                    ? 'var(--accent-glow)' 
                                    : 'transparent',
                            color: isHighlighted || selected ? 'var(--accent)' : 'var(--text-primary)',
                            fontWeight: selected ? '600' : 'normal'
                        }}
                        onMouseEnter={() => {
                            setHighlightedOptionIndex(idx);
                        }}
                    >
                        <span>{val === "?" ? "? (ANY)" : val === "!" ? "! (NOT USED)" : val}</span>
                        {selected && <i className="fa-solid fa-check" style={{ fontSize: '0.75rem' }}></i>}
                    </div>
                );
            })}
        </div>,
        document.body
    );
}

/**
 * VirtualRow - Row for adding new rules inline
 */
function VirtualRow({
    draftRuleSetData,
    featuresMetadata,
    focusedCell,
    isEditing,
    editingCellValue,
    dropdownCoords,
    highlightedOptionIndex,
    setFocusedCell,
    setIsEditing,
    setEditingCellValue,
    setHighlightedOptionIndex,
    handleCellKeyDown,
    handleCommitCell,
    handleEditorKeyDown,
    toggleValue,
    getSelectedValues
}) {
    const virtualRowIndex = draftRuleSetData.rows.length;

    return (
        <tr key="virtual-row" style={{ opacity: 0.6 }}>
            <td style={{ textAlign: "center", color: "var(--text-secondary)", fontSize: "0.85rem", userSelect: "none" }}>
                <i className="fa-solid fa-plus"></i>
            </td>
            {draftRuleSetData.headers.map((header, colIdx) => {
                const colIndex = colIdx + 1;
                const valueToShow = '';
                
                const isFocused = focusedCell?.rowIndex === virtualRowIndex && focusedCell?.colIndex === colIndex;
                const isCellEditing = isEditing && isFocused;
                
                const featureMeta = featuresMetadata.find(v => v.name === header);

                return (
                    <td 
                        key={header}
                        data-cell-row={virtualRowIndex}
                        data-cell-col={colIndex}
                        tabIndex={0}
                        onFocus={() => setFocusedCell({ rowIndex: virtualRowIndex, colIndex })}
                        onKeyDown={(e) => handleCellKeyDown(e, virtualRowIndex, colIndex)}
                        onDoubleClick={() => setIsEditing(true)}
                        className={`grid-cell ${isFocused ? 'focused' : ''}`}
                        style={{
                            outline: isFocused ? '2px solid var(--accent)' : 'none',
                            background: isFocused ? 'rgba(99, 102, 241, 0.15)' : 'transparent',
                            position: 'relative',
                            cursor: 'pointer',
                            minWidth: '120px',
                            padding: isCellEditing ? '0px' : '0.5rem 0.75rem',
                            verticalAlign: 'middle'
                        }}
                    >
                        {isCellEditing ? (
                            <RuleCellEditor
                                featureMeta={featureMeta}
                                editingCellValue={editingCellValue}
                                setEditingCellValue={setEditingCellValue}
                                handleCommitCell={() => handleCommitCell(virtualRowIndex, header)}
                                handleEditorKeyDown={(e) => handleEditorKeyDown(e, virtualRowIndex, header)}
                                dropdownCoords={dropdownCoords}
                                highlightedOptionIndex={highlightedOptionIndex}
                                setHighlightedOptionIndex={setHighlightedOptionIndex}
                                toggleValue={toggleValue}
                                getSelectedValues={getSelectedValues}
                            />
                        ) : (
                            <div style={{ userSelect: 'none', fontStyle: 'italic', color: 'var(--text-secondary)' }}>
                                {valueToShow}
                            </div>
                        )}
                    </td>
                );
            })}
        </tr>
    );
}

/**
 * UnsavedChangesAlert - Alert bar for unsaved changes
 */
function UnsavedChangesAlert({ originalRuleSetData, handleSaveChanges, setDraftRuleSetData }) {
    const { canEditCurrent } = useToolAccess();
    return (
        <div className="fade-in" style={{
            display: 'flex', 
            gap: '1rem', 
            alignItems: 'center', 
            background: 'rgba(99, 102, 241, 0.1)', 
            border: '1px solid var(--accent)', 
            padding: '1rem', 
            borderRadius: 'var(--radius)', 
            marginTop: '1.5rem', 
            justifyContent: 'space-between'
        }}>
            <div>
                <strong style={{ color: 'var(--accent)' }}>You have unsaved changes!</strong>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', marginTop: '0.25rem' }}>
                    These edits are local and won't affect simulators or other users until saved.
                </p>
            </div>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button 
                    className="btn-secondary" 
                    onClick={() => setDraftRuleSetData(JSON.parse(JSON.stringify(originalRuleSetData)))} 
                    style={{ fontSize: '0.85rem' 
                }}>
                    Discard
                </button>
                <button 
                    className="btn-primary" 
                    onClick={handleSaveChanges} 
                    disabled={!canEditCurrent}
                    style={{ fontSize: '0.85rem', background: 'var(--accent)', color: 'white' 
                }}>
                    Save changes
                </button>
            </div>
        </div>
    );
}

/**
 * NavigationBlockerModal - Modal for blocking navigation with unsaved changes
 */
function NavigationBlockerModal({ blocker, hasChanges, originalRuleSetData, handleSaveChanges, setDraftRuleSetData }) {
    const { canEditCurrent } = useToolAccess();
    if (blocker.state !== "blocked") return null;

    return (
        <ModalPortal>
        <div className="modal-overlay fade-in">
            <div className="modal-content" style={{ maxWidth: '400px', padding: '1.25rem' }}>
                <h2 style={{ fontSize: '1.5rem', marginBottom: '0.5rem' }}>Unsaved Changes</h2>
                <p style={{ color: 'var(--text-secondary)', fontSize: '0.95rem', lineHeight: '1.4' }}>
                    You have unsaved changes in this rule table. Would you like to save before leaving?
                </p>
                <div className="modal-actions" style={{ display: 'flex', gap: '0.5rem', marginTop: '2rem' }}>
                    <button 
                        onClick={async () => {
                            await handleSaveChanges();
                            blocker.proceed();
                        }} 
                        className="btn-primary" 
                        disabled={!canEditCurrent}
                        style={{ flex: 1, padding: '0.75rem' }}
                    >
                        Save
                    </button>
                    <button 
                        onClick={() => {
                            setDraftRuleSetData(JSON.parse(JSON.stringify(originalRuleSetData)));
                            blocker.proceed();
                        }} 
                        className="btn-secondary" 
                        style={{ borderColor: 'var(--error)', color: 'var(--error)', flex: 1, padding: '0.75rem' }}
                    >
                        Discard
                    </button>
                    <button 
                        onClick={() => blocker.reset()} 
                        className="btn-secondary" 
                        style={{ flex: 1, padding: '0.75rem' }}
                    >
                        Cancel
                    </button>
                </div>
            </div>
        </div>
        </ModalPortal>
    );
}
